import {
  // `Blocks` is the universal fallback used by getModuleIcon/getNavIcon
  // when a name is not present in the registry.
  Blocks,
  // Extra icons used by non-module code (DB seeds, fixtures, dashboards,
  // standalone pages) — NOT referenced by any `modules/*\/index.ts` so the
  // codegen does not pick them up. Keep this list in sync with runtime usage.
  Brain,
  Car,
  CarTaxiFront,
  FileCheck,
  GitBranch,
  Layers,
  Mail,
  ShoppingBag,
  TrendingDown,
  Truck,
  type LucideIcon,
} from "lucide-react";
import { generatedIconRegistry } from "./generated/icon-registry";

// ---------------------------------------------------------------------------
// Shared icon registry
// Maps icon name strings (from module defineModule() config) to Lucide
// components. The bulk of the map is AUTO-GENERATED from `modules/*\/index.ts`
// — run `pnpm gen:modules` after adding or removing a module icon.
//
// Only add icons to the `extraIcons` map below if they are referenced outside
// of any module (DB seed, dashboard card, standalone page, fixture, etc.).
// ---------------------------------------------------------------------------

const extraIcons: Record<string, LucideIcon> = {
  Brain,
  Car,
  CarTaxiFront,
  FileCheck,
  GitBranch,
  Layers,
  // `Mail` is used by the `gmail` connector-type seed (migration 006) — the
  // `connectors_types.icon` column stores the string `'Mail'` so the admin UI
  // can render it via `getModuleIcon(...)`. Codegen doesn't pick it up from
  // any `modules/*/index.ts` navigation, so we register it here.
  Mail,
  // `ShoppingBag` — used by the `shopee` connector-type seed (migration 007).
  // Same rationale as `Mail` above: the connectors_types.icon column stores
  // the literal string `'ShoppingBag'`; codegen doesn't see it because the
  // module itself uses a different top-level icon.
  ShoppingBag,
  TrendingDown,
  Truck,
};

const iconRegistry: Record<string, LucideIcon> = {
  ...generatedIconRegistry,
  ...extraIcons,
};

/**
 * Resolve a Lucide icon component from its name string.
 * Falls back to `Blocks` if the name is not registered.
 */
export function getModuleIcon(iconName: string): LucideIcon {
  return iconRegistry[iconName] || Blocks;
}

/**
 * Resolve a navigation item icon.
 * Logs a warning in dev if the icon name is missing from the registry so
 * misses are surfaced early instead of silently falling back.
 */
export function getNavIcon(iconName: string): LucideIcon {
  const icon = iconRegistry[iconName];
  if (!icon) {
    if (!import.meta.env.PROD) {
      // eslint-disable-next-line no-console
      console.warn(`[icon-registry] Missing nav icon "${iconName}" — add it to apps/web/src/lib/icon-registry.ts`);
    }
    return Blocks;
  }
  return icon;
}

// ---------------------------------------------------------------------------
// Module colour helpers
// ---------------------------------------------------------------------------

const MODULE_COLORS: Record<string, string> = {
  settings: "#6B7280",
};

const DEFAULT_MODULE_COLOR = "#2490EF";

export function getModuleColor(moduleId: string): string {
  return MODULE_COLORS[moduleId] || DEFAULT_MODULE_COLOR;
}

// ---------------------------------------------------------------------------
// Hook: useModuleConfig — single source of truth for module icon + color
// ---------------------------------------------------------------------------

import { usePlatformModules } from "@kubdee/sdk/hooks/use-settings";

/**
 * Returns resolved icon (LucideIcon) and color for a given moduleId
 * from the platform modules API (DB).
 */
export function useModuleConfig(moduleId: string) {
  const { data: platformModulesRes } = usePlatformModules();
  const platformModules = platformModulesRes?.data ?? [];
  const mod = platformModules.find((m) => m.id === moduleId);

  return {
    icon: getModuleIcon(mod?.icon ?? moduleId),
    color: mod?.color ?? getModuleColor(moduleId),
    name: mod?.name ?? moduleId,
    nameTh: mod?.nameTh ?? null,
  };
}
